import { useState } from 'react';
import { ShoppingCart, Download, Truck, DollarSign, Package } from 'lucide-react';
import { useReorder } from '../hooks/useData';
import { formatCurrency, getStatusColor } from '../utils/api';

function VendorOrderCard({ vendor, data, onToggleItem, selectedItems }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Truck className="text-blue-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{vendor}</h3>
            <p className="text-sm text-gray-500">{data.sku_count} SKUs</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-gray-900">{formatCurrency(data.total_value)}</p>
          <p className="text-sm text-gray-500">Total order value</p>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <table className="w-full">
            <thead>
              <tr className="text-xs text-gray-500 uppercase">
                <th className="text-left py-2">
                  <input
                    type="checkbox"
                    checked={data.items.every(item => selectedItems.includes(item.sku))}
                    onChange={(e) => {
                      data.items.forEach(item => onToggleItem(item.sku, e.target.checked));
                    }}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="text-left py-2">SKU</th>
                <th className="text-right py-2">Current</th>
                <th className="text-right py-2">Order Qty</th>
                <th className="text-right py-2">Value</th>
                <th className="text-center py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.sku} className="border-t border-gray-50">
                  <td className="py-3">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.sku)}
                      onChange={(e) => onToggleItem(item.sku, e.target.checked)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="py-3">
                    <p className="font-medium text-gray-900">{item.sku}</p>
                    <p className="text-xs text-gray-500">{item.brand}</p>
                  </td>
                  <td className="py-3 text-right text-gray-600">
                    {item.current_stock}
                    <span className="text-xs text-gray-400 ml-1">
                      ({item.days_of_stock}d)
                    </span>
                  </td>
                  <td className="py-3 text-right font-medium text-blue-600">
                    {item.suggested_order_qty}
                  </td>
                  <td className="py-3 text-right text-gray-900">
                    {formatCurrency(item.order_value)}
                  </td>
                  <td className="py-3 text-center">
                    <span className={getStatusColor(item.status)}>
                      {item.status?.replace('_', ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Reorder() {
  const { data, isLoading, error } = useReorder();
  const [selectedItems, setSelectedItems] = useState([]);

  const handleToggleItem = (sku, selected) => {
    if (selected) {
      setSelectedItems([...selectedItems, sku]);
    } else {
      setSelectedItems(selectedItems.filter(s => s !== sku));
    }
  };

  const handleSelectAll = () => {
    if (data?.recommendations) {
      setSelectedItems(data.recommendations.map(r => r.sku));
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems([]);
  };

  const selectedTotal = data?.recommendations
    ?.filter(r => selectedItems.includes(r.sku))
    ?.reduce((sum, r) => sum + r.order_value, 0) || 0;

  const handleExportCSV = () => {
    if (!data?.recommendations) return;

    const items = data.recommendations.filter(r =>
      selectedItems.length === 0 || selectedItems.includes(r.sku)
    );

    const headers = ['SKU', 'Product', 'Brand', 'Vendor', 'Current Stock', 'Order Qty', 'Unit Cost', 'Total Value', 'Lead Time', 'Status'];
    const rows = items.map(item => [
      item.sku,
      item.product_name,
      item.brand,
      item.vendor,
      item.current_stock,
      item.suggested_order_qty,
      item.cost,
      item.order_value,
      item.lead_time_days,
      item.status
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reorder_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-lg">
        Error loading reorder data: {error.message}
      </div>
    );
  }

  const { summary, recommendations } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reorder Recommendations</h1>
          <p className="text-gray-500">Purchase order suggestions based on forecasts</p>
        </div>
        <button
          onClick={handleExportCSV}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <Package className="text-red-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Critical</p>
              <p className="text-xl font-bold text-gray-900">{summary.critical_count}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 rounded-lg">
              <Package className="text-orange-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Order Now</p>
              <p className="text-xl font-bold text-gray-900">{summary.order_now_count}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <ShoppingCart className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total SKUs</p>
              <p className="text-xl font-bold text-gray-900">{summary.total_skus_need_reorder}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <DollarSign className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Value</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.total_order_value)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Selection Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleSelectAll}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
            >
              Select All
            </button>
            <button
              onClick={handleDeselectAll}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
            >
              Deselect All
            </button>
            <span className="text-sm text-gray-500">
              {selectedItems.length} items selected
            </span>
          </div>
          {selectedItems.length > 0 && (
            <div className="text-right">
              <p className="text-sm text-gray-500">Selected Order Value</p>
              <p className="text-xl font-bold text-blue-600">{formatCurrency(selectedTotal)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Vendor Groups */}
      <div className="space-y-4">
        {Object.entries(summary.by_vendor || {}).map(([vendor, data]) => (
          <VendorOrderCard
            key={vendor}
            vendor={vendor}
            data={data}
            selectedItems={selectedItems}
            onToggleItem={handleToggleItem}
          />
        ))}
      </div>

      {/* Empty State */}
      {recommendations?.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center py-12">
          <ShoppingCart className="mx-auto text-gray-300" size={48} />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No reorders needed</h3>
          <p className="mt-2 text-gray-500">All inventory levels are healthy</p>
        </div>
      )}
    </div>
  );
}
